/**
 * Treatments Page
 * Vista para gestionar el catálogo de plagas, planes de actuación y materiales
 */

import { useState } from "react";
import { Icon, ConfirmModal, PestModal } from "@/components/ui";
import styles from "./TreatmentsPage.module.css";

interface ActionStep {
  id: string;
  order: number;
  description: string;
}

interface Material {
  id: string;
  name: string;
  type:
    | "Producto Químico"
    | "Herramienta"
    | "Equipo de Protección"
    | "Material General";
  quantity: number;
  required: boolean; // Si es obligatorio llevarlo
}

interface Pest {
  id: string;
  name: string;
  category: "Insectos" | "Roedores" | "Aves" | "Otros";
  icon?: string;
  actionPlan: ActionStep[]; // Plan de actuación ordenado
  materials: Material[]; // Materiales y productos necesarios
  preventions: string[]; // Medidas preventivas
  image?: string;
}

const mockPests: Pest[] = [
  {
    id: "1",
    name: "Cucarachas",
    category: "Insectos",
    icon: "🪳",
    actionPlan: [
      {
        id: "1",
        order: 1,
        description:
          "Inspección completa de la zona afectada y áreas adyacentes",
      },
      {
        id: "2",
        order: 2,
        description: "Identificar puntos de entrada y refugios",
      },
      {
        id: "3",
        order: 3,
        description:
          "Aplicar gel insecticida en grietas, hendiduras y zonas de paso",
      },
      {
        id: "4",
        order: 4,
        description: "Colocar trampas con atrayente en puntos estratégicos",
      },
      {
        id: "5",
        order: 5,
        description: "Pulverización residual en zócalos y rincones",
      },
      {
        id: "6",
        order: 6,
        description: "Documentar tratamiento y tomar fotografías",
      },
      {
        id: "7",
        order: 7,
        description: "Informar al cliente de medidas preventivas",
      },
    ],
    materials: [
      {
        id: "1",
        name: "Gel insecticida Maxforce",
        type: "Producto Químico",
        quantity: 2,
        required: true,
      },
      {
        id: "2",
        name: "Insecticida residual Cipermetrina 10%",
        type: "Producto Químico",
        quantity: 500,
        required: true,
      },
      {
        id: "3",
        name: "Trampas adhesivas con atrayente",
        type: "Material General",
        quantity: 6,
        required: true,
      },
      {
        id: "4",
        name: "Pulverizador manual 2L",
        type: "Herramienta",
        quantity: 1,
        required: true,
      },
      {
        id: "5",
        name: "Mascarilla FFP2",
        type: "Equipo de Protección",
        quantity: 1,
        required: true,
      },
      {
        id: "6",
        name: "Guantes nitrilo",
        type: "Equipo de Protección",
        quantity: 1,
        required: true,
      },
      {
        id: "7",
        name: "Linterna",
        type: "Herramienta",
        quantity: 1,
        required: false,
      },
    ],
    preventions: [
      "Limpieza exhaustiva de restos de comida",
      "Sellado de grietas y fisuras",
      "Eliminación de humedad",
      "Almacenamiento adecuado de alimentos",
    ],
  },
  {
    id: "2",
    name: "Ratas",
    category: "Roedores",
    icon: "🐀",
    actionPlan: [
      {
        id: "1",
        order: 1,
        description: "Inspección exterior e interior del perímetro",
      },
      {
        id: "2",
        order: 2,
        description: "Localizar madrigueras, senderos y puntos de acceso",
      },
      {
        id: "3",
        order: 3,
        description:
          "Colocar cebaderos seguros con rodenticida en puntos estratégicos",
      },
      {
        id: "4",
        order: 4,
        description: "Instalar trampas mecánicas en zonas de alto tránsito",
      },
      {
        id: "5",
        order: 5,
        description: "Tratar madrigueras activas con rodenticida",
      },
      {
        id: "6",
        order: 6,
        description: "Señalizar ubicaciones de cebaderos y trampas",
      },
      {
        id: "7",
        order: 7,
        description: "Completar registro de tratamiento y mapa de ubicaciones",
      },
    ],
    materials: [
      {
        id: "1",
        name: "Rodenticida anticoagulante bloques",
        type: "Producto Químico",
        quantity: 2,
        required: true,
      },
      {
        id: "2",
        name: "Estaciones porta-cebos seguras",
        type: "Material General",
        quantity: 8,
        required: true,
      },
      {
        id: "3",
        name: "Trampas mecánicas profesionales",
        type: "Herramienta",
        quantity: 4,
        required: true,
      },
      {
        id: "4",
        name: "Señales de advertencia cebaderos",
        type: "Material General",
        quantity: 8,
        required: true,
      },
      {
        id: "5",
        name: "Guantes resistentes",
        type: "Equipo de Protección",
        quantity: 1,
        required: true,
      },
      {
        id: "6",
        name: "Espátula y cepillo",
        type: "Herramienta",
        quantity: 1,
        required: false,
      },
    ],
    preventions: [
      "Sellado de accesos (> 1 cm)",
      "Eliminación de fuentes de alimento",
      "Control de basuras",
      "Mantenimiento de desagües",
    ],
  },
];

const getCategoryIcon = (category: Pest["category"]): string => {
  const icons = {
    Insectos: "bug",
    Roedores: "activity",
    Aves: "feather",
    Otros: "help-circle",
  };
  return icons[category] || "help-circle";
};

export function TreatmentsPage() {
  const [pests, setPests] = useState<Pest[]>(mockPests);
  const [selectedPest, setSelectedPest] = useState<Pest | null>(mockPests[0]);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("Todas");
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPest, setEditingPest] = useState<Pest | null>(null);
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: "info" | "warning" | "danger" | "success";
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: "",
    message: "",
    type: "info",
    onConfirm: () => {},
  });

  const categories = ["Todas", "Insectos", "Roedores", "Aves", "Otros"];

  const filteredPests = pests.filter((pest) => {
    const matchesSearch =
      searchQuery === "" ||
      pest.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory =
      categoryFilter === "Todas" || pest.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const handleNewPest = () => {
    setEditingPest(null);
    setShowEditModal(true);
  };

  const handleEditPest = (pest: Pest) => {
    setEditingPest(pest);
    setShowEditModal(true);
  };

  const handleDeletePest = (pestId: string) => {
    const pest = pests.find((p) => p.id === pestId);
    setConfirmModal({
      isOpen: true,
      title: "Eliminar Plaga",
      message: `¿Estás seguro de que deseas eliminar "${pest?.name}"? Esta acción no se puede deshacer.`,
      type: "danger",
      onConfirm: () => {
        const newPests = pests.filter((p) => p.id !== pestId);
        setPests(newPests);
        // Si se elimina la plaga seleccionada, seleccionar otra o null
        if (selectedPest?.id === pestId) {
          setSelectedPest(newPests.length > 0 ? newPests[0] : null);
        }
        setConfirmModal({ ...confirmModal, isOpen: false });
      },
    });
  };

  const handleSavePest = (pest: Pest) => {
    if (editingPest) {
      // Editar existente
      setPests(pests.map((p) => (p.id === pest.id ? pest : p)));
      if (selectedPest?.id === pest.id) {
        setSelectedPest(pest);
      }
    } else {
      // Crear nuevo
      const newPest = { ...pest, id: String(Date.now()) };
      setPests([...pests, newPest]);
      setSelectedPest(newPest);
    }
    setShowEditModal(false);
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.headerTitle}>
            <Icon name="bug" size={28} />
            <div>
              <h1>Catálogo de Plagas</h1>
              <p>Gestión de plagas, planes de actuación y materiales</p>
            </div>
          </div>
          <button className={styles.newButton} onClick={handleNewPest}>
            <Icon name="plus" size={20} />
            Nueva Plaga
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className={styles.filters}>
        <div className={styles.searchBar}>
          <Icon name="search" size={20} className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Buscar por nombre o nombre científico..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={styles.searchInput}
          />
        </div>

        <div className={styles.categoryFilters}>
          {categories.map((category) => (
            <button
              key={category}
              className={`${styles.categoryButton} ${
                categoryFilter === category ? styles.active : ""
              }`}
              onClick={() => setCategoryFilter(category)}
            >
              {category}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className={styles.content}>
        {/* Pests List */}
        <div className={styles.pestsList}>
          {filteredPests.map((pest) => (
            <div
              key={pest.id}
              className={`${styles.pestCard} ${
                selectedPest?.id === pest.id ? styles.selected : ""
              }`}
              onClick={() => setSelectedPest(pest)}
            >
              <div className={styles.pestCardHeader}>
                {pest.icon ? (
                  <div className={styles.pestIconEmoji}>{pest.icon}</div>
                ) : (
                  <div className={styles.pestIcon}>
                    <Icon name={getCategoryIcon(pest.category)} size={24} />
                  </div>
                )}
                <div className={styles.pestInfo}>
                  <h3>{pest.name}</h3>
                </div>
              </div>
              <div className={styles.pestCardFooter}>
                <span className={styles.categoryBadge}>{pest.category}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Pest Details */}
        {selectedPest && (
          <div className={styles.pestDetails}>
            <div className={styles.detailsHeader}>
              <div className={styles.detailsHeaderRow}>
                <div className={styles.detailsTitle}>
                  {selectedPest.icon ? (
                    <div className={styles.detailsIconEmoji}>
                      {selectedPest.icon}
                    </div>
                  ) : (
                    <Icon
                      name={getCategoryIcon(selectedPest.category)}
                      size={32}
                    />
                  )}
                  <div>
                    <h2>{selectedPest.name}</h2>
                    <span className={styles.categoryBadge}>
                      {selectedPest.category}
                    </span>
                  </div>
                </div>
                <div className={styles.detailsActions}>
                  <button
                    className={styles.editButton}
                    onClick={() => handleEditPest(selectedPest)}
                    title="Editar"
                  >
                    <Icon name="edit" size={18} />
                  </button>
                  <button
                    className={styles.deleteButton}
                    onClick={() => handleDeletePest(selectedPest.id)}
                    title="Eliminar"
                  >
                    <Icon name="xCircle" size={18} />
                  </button>
                </div>
              </div>
            </div>

            <div className={styles.detailsContent}>
              {/* Action Plan - NEW */}
              {selectedPest.actionPlan &&
                selectedPest.actionPlan.length > 0 && (
                  <section className={styles.section}>
                    <h3>
                      <Icon name="list" size={20} />
                      Plan de Actuación
                    </h3>
                    <div className={styles.actionPlan}>
                      {selectedPest.actionPlan
                        .sort((a, b) => a.order - b.order)
                        .map((step) => (
                          <div key={step.id} className={styles.actionStep}>
                            <div className={styles.stepNumber}>
                              {step.order}
                            </div>
                            <div className={styles.stepContent}>
                              <p className={styles.stepDescription}>
                                {step.description}
                              </p>
                            </div>
                          </div>
                        ))}
                    </div>
                  </section>
                )}

              {/* Materials - NEW */}
              {selectedPest.materials && selectedPest.materials.length > 0 && (
                <section className={styles.section}>
                  <h3>
                    <Icon name="package" size={20} />
                    Materiales Necesarios
                  </h3>
                  <div className={styles.materialsGrid}>
                    {selectedPest.materials.map((material) => (
                      <div
                        key={material.id}
                        className={`${styles.materialCard} ${material.required ? styles.required : ""}`}
                      >
                        <div className={styles.materialHeader}>
                          <span className={styles.materialName}>
                            {material.name}
                          </span>
                          {material.required && (
                            <span className={styles.requiredBadge}>
                              Obligatorio
                            </span>
                          )}
                        </div>
                        <div className={styles.materialDetails}>
                          <span className={styles.materialType}>
                            {material.type}
                          </span>
                          <span className={styles.materialQuantity}>
                            {material.quantity}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Preventions */}
              <section className={styles.section}>
                <h3>
                  <Icon name="checkCircle" size={20} />
                  Medidas Preventivas
                </h3>
                <ul>
                  {selectedPest.preventions.map((prevention, index) => (
                    <li key={index}>{prevention}</li>
                  ))}
                </ul>
              </section>
            </div>
          </div>
        )}
      </div>

      {/* Confirm Modal */}
      {confirmModal.isOpen && (
        <ConfirmModal
          isOpen={confirmModal.isOpen}
          title={confirmModal.title}
          message={confirmModal.message}
          type={confirmModal.type}
          onConfirm={confirmModal.onConfirm}
          onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        />
      )}

      {/* Pest Edit/Create Modal */}
      <PestModal
        isOpen={showEditModal}
        pest={editingPest}
        onClose={() => setShowEditModal(false)}
        // @ts-ignore - Pest type id optionality conflict
        onSave={handleSavePest}
      />
    </div>
  );
}
